import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LegalPage from './pages/LegalPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import Dashboard from './pages/Dashboard';
import TicketsPage from './pages/TicketsPage';
import NewRequestPage from './pages/NewRequestPage';
import TicketDetailPage from './pages/TicketDetailPage';
import CreditsPage from './pages/CreditsPage';
import FileCheckerPage from './pages/FileCheckerPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTicketsPage from './pages/admin/AdminRequestsPage';
import AdminTicketDetailPage from './pages/admin/AdminTicketDetailPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSolutionsPage from './pages/admin/AdminSolutionsPage';
import AdminServicesPage from './pages/admin/AdminServicesPage';
import AdminSlaveToolsPage from './pages/admin/AdminSlaveToolsPage';
import AdminBusinessHoursPage from './pages/admin/AdminBusinessHoursPage';
import AdminInvoicesPage from './pages/admin/AdminInvoicesPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminSmsLogsPage from './pages/admin/AdminSmsLogsPage';
import SlaveManagerPage from './pages/SlaveManagerPage';
import TrabajosPage from './pages/TrabajosPage';
import AdminShowcasePage from './pages/admin/AdminShowcasePage';
import FlexJobsPage from './pages/FlexJobsPage';
import OriSearchPage from './pages/OriSearchPage';
import PowerCatalogPage from './pages/PowerCatalogPage';
import MonitorPage from './pages/MonitorPage';
import BoschLookupPage from './pages/BoschLookupPage';
import InvoicesPage from './pages/InvoicesPage';
import VehicleDatabasePage from './pages/VehicleDatabasePage';
import PCodesPage from './pages/PCodesPage';

import './App.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/legal/:page" element={<LegalPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><TicketsPage /></ProtectedRoute>} />
      <Route path="/tickets/new" element={<ProtectedRoute><NewRequestPage /></ProtectedRoute>} />
      <Route path="/tickets/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
      <Route path="/file-checker" element={<ProtectedRoute><FileCheckerPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/slave-manager" element={<ProtectedRoute><SlaveManagerPage /></ProtectedRoute>} />
      <Route path="/flex-jobs" element={<ProtectedRoute><FlexJobsPage /></ProtectedRoute>} />
      <Route path="/ori-search" element={<ProtectedRoute><OriSearchPage /></ProtectedRoute>} />
      <Route path="/catalog" element={<ProtectedRoute><PowerCatalogPage /></ProtectedRoute>} />
      <Route path="/bosch-lookup" element={<ProtectedRoute><BoschLookupPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/vehicles" element={<ProtectedRoute><VehicleDatabasePage /></ProtectedRoute>} />
      <Route path="/pcodes" element={<ProtectedRoute><PCodesPage /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/tickets" element={<ProtectedRoute adminOnly><AdminTicketsPage /></ProtectedRoute>} />
      <Route path="/admin/tickets/:ticketId" element={<ProtectedRoute adminOnly><AdminTicketDetailPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/solutions" element={<ProtectedRoute adminOnly><AdminSolutionsPage /></ProtectedRoute>} />
      <Route path="/admin/services" element={<ProtectedRoute adminOnly><AdminServicesPage /></ProtectedRoute>} />
      <Route path="/admin/slave-tools" element={<ProtectedRoute adminOnly><AdminSlaveToolsPage /></ProtectedRoute>} />
      <Route path="/admin/business-hours" element={<ProtectedRoute adminOnly><AdminBusinessHoursPage /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute adminOnly><AdminInvoicesPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/sms-logs" element={<ProtectedRoute adminOnly><AdminSmsLogsPage /></ProtectedRoute>} />

      <Route path="/monitor/:token" element={<MonitorPage />} />
      <Route path="/trabajos" element={<TrabajosPage />} />
      <Route path="/admin/showcase" element={<ProtectedRoute adminOnly><AdminShowcasePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
